import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { DeskTable, StackedDateTime, muted, type Column } from '@/components/common/DeskTable'
import FacetFilter from '@/components/common/FacetFilter'
import FilterBarApply from '@/components/common/FilterBarApply'
import FilterBarForm from '@/components/common/FilterBarForm'
import FilterSearch from '@/components/common/FilterSearch'
import { Field } from '@/components/ui/field'
import { GlassSelect } from '@/components/ui/glass-select'
import { Input } from '@/components/ui/input'
import PageFrame from '@/components/common/PageFrame'
import { FILTER_BAR } from '@/lib/ui-shell'
import ShortId from '@/components/common/ShortId'
import StatusBadge from '@/components/common/StatusBadge'
import { accessOf, canOpening } from '@/lib/admin-access'
import { backofficeApi, type ActivityRow, type EkycRow } from '@/lib/api-client'
import { emptyFacet, facetToQuery, type FacetValue } from '@/lib/facet'
import { dash } from '@/lib/format'
import { facetFromSearch, writeFacet, writeSort } from '@/lib/desk-url'
import { useDeskSort } from '@/hooks/useDeskSort'
import { actionLabel, humanize, optionLabels, statusLabel } from '@/lib/labels'
import { ACTIVITY_WIDTH, EKYC_WIDTH } from '@/lib/column-widths'
import { useListPageSize } from '@/hooks/useListPageSize'
import { resolveOffsetPagination } from '@/lib/list-pagination'
import { DESK_REFETCH_MS, isFilterChangeFetching, visibleTabRefetch } from '@/lib/query-client'
import { useAuthStore } from '@/store/auth'

const clip = 'overflow-hidden'
const stacked = 'overflow-hidden whitespace-normal'

const activityColumns: Column<ActivityRow>[] = [
  { key: 'when', sortKey: 'when', sortFirst: 'desc', header: 'When', width: ACTIVITY_WIDTH.when, className: stacked, mobile: 'subtitle', cell: (r) => <StackedDateTime value={r.created_at} /> },
  { key: 'action', sortKey: 'action', header: 'Action', width: ACTIVITY_WIDTH.action, mobile: 'title', cell: (r) => actionLabel(r.action) },
  {
    key: 'actor',
    header: 'Actor',
    width: ACTIVITY_WIDTH.actor,
    className: clip,
    mobile: 'field',
    cell: (r) => (r.actor_email ? <span className="block truncate" title={r.actor_email}>{r.actor_email}</span> : <ShortId value={r.actor_id} />),
  },
  {
    key: 'type',
    sortKey: 'type',
    header: 'Type',
    width: ACTIVITY_WIDTH.type,
    className: clip,
    cell: (r) => {
      const label = humanize(r.target_type || '')
      return <span className="block truncate" title={label || undefined}>{muted(label)}</span>
    },
  },
  { key: 'target', header: 'Target', width: ACTIVITY_WIDTH.target, className: clip, mobile: 'field', cell: (r) => <ShortId value={r.target_id} /> },
]

/** The client name links to the review only when there is a client to open; a row click would be dead for the rest. */
function ekycColumns(canOpenReview: boolean): Column<EkycRow>[] {
  return [
    { key: 'when', sortKey: 'when', sortFirst: 'desc', header: 'When', width: EKYC_WIDTH.when, className: stacked, mobile: 'subtitle', cell: (r) => <StackedDateTime value={r.created_at} /> },
    {
      key: 'client',
      sortKey: 'client',
      header: 'Client',
      sticky: true,
      width: EKYC_WIDTH.client,
      className: clip,
      mobile: 'title',
      cell: (r) =>
        canOpenReview && r.client_id ? (
          <Link to={`/opening/agreements/${r.client_id}`} className="block truncate font-medium text-link underline-offset-2 hover:underline" title={r.full_name || undefined}>
            {dash(r.full_name)}
          </Link>
        ) : (
          <span className="block truncate font-medium" title={r.full_name || undefined}>{dash(r.full_name)}</span>
        ),
    },
    { key: 'code', sortKey: 'code', header: 'Code', width: EKYC_WIDTH.code, mobile: 'field', cell: (r) => <span className="font-mono">{muted(dash(r.client_code))}</span> },
    { key: 'vendor', sortKey: 'vendor', header: 'Vendor', width: EKYC_WIDTH.vendor, mobile: 'field', cell: (r) => humanize(r.vendor || '') },
    { key: 'status', sortKey: 'status', header: 'Status', width: EKYC_WIDTH.status, className: 'whitespace-normal', mobile: 'status', cell: (r) => <StatusBadge className="whitespace-normal" status={r.status} /> },
    { key: 'id', header: 'Attempt ID', width: EKYC_WIDTH.id, className: clip, cell: (r) => <ShortId value={r.id} /> },
  ]
}

function useListParams() {
  const [sp, setSp] = useSearchParams()
  const page = Number(sp.get('page') || '1')
  const pageSize = useListPageSize()
  const setParams = (next: Record<string, string>) => {
    const cur = new URLSearchParams(sp)
    for (const [k, v] of Object.entries(next)) cur.set(k, v)
    setSp(cur, { replace: true })
  }
  return { sp, setSp, page, pageSize, setParams }
}

type ActivityFilters = { action: FacetValue; actor: string; target: string; from: string; to: string }

const NO_ACTIVITY_FILTERS: ActivityFilters = { action: emptyFacet(), actor: '', target: '', from: '', to: '' }

function activityFromSearch(sp: URLSearchParams): ActivityFilters {
  return {
    action: facetFromSearch(sp, 'action'),
    actor: sp.get('actor_id') || '',
    target: sp.get('target_id') || '',
    from: sp.get('date_from') || '',
    to: sp.get('date_to') || '',
  }
}

const sameFilters = (a: ActivityFilters, b: ActivityFilters) => JSON.stringify(a) === JSON.stringify(b)

export function ActivityLogsPage() {
  const role = accessOf(useAuthStore((s) => s.user))
  const scope = canOpening(role) ? 'opening' : 'settlement'
  const { sp, setSp, page, pageSize, setParams } = useListParams()
  const { sort, onSortChange, sortQuery } = useDeskSort(['when', 'action', 'type'])
  const spKey = sp.toString()
  const applied = activityFromSearch(sp)
  const [draft, setDraft] = useState<ActivityFilters>(applied)
  useEffect(() => {
    setDraft(activityFromSearch(new URLSearchParams(spKey)))
  }, [spKey])
  const options = useQuery({
    queryKey: ['activity-options', scope],
    queryFn: () => backofficeApi.activityOptions(scope),
    staleTime: 5 * 60_000,
  })
  const filterParams = {
    ...facetToQuery('action', applied.action),
    actor_id: applied.actor || undefined,
    target_id: applied.target.trim() || undefined,
    date_from: applied.from || undefined,
    date_to: applied.to || undefined,
  }
  const q = useQuery({
    queryKey: ['activity-logs', scope, page, pageSize, sortQuery, filterParams],
    queryFn: () => backofficeApi.activityLogs(scope, { page, page_size: pageSize, ...sortQuery, ...filterParams }),
    placeholderData: keepPreviousData,
    ...visibleTabRefetch(DESK_REFETCH_MS),
  })
  const items = q.data?.items ?? []
  const paging = resolveOffsetPagination({
    page,
    pageSize,
    total: q.data?.total,
    rowCount: items.length,
    hasMore: false,
  })
  const badRange = Boolean(draft.from && draft.to && draft.from > draft.to)
  const filtered = !sameFilters(applied, NO_ACTIVITY_FILTERS)

  function commit(next: ActivityFilters) {
    const cur = new URLSearchParams()
    writeFacet(cur, 'action', next.action)
    if (next.actor) cur.set('actor_id', next.actor)
    if (next.target.trim()) cur.set('target_id', next.target.trim())
    if (next.from) cur.set('date_from', next.from)
    if (next.to) cur.set('date_to', next.to)
    cur.set('page', '1')
    cur.set('page_size', String(pageSize))
    writeSort(cur, sort)
    setSp(cur, { replace: true })
  }

  return (
    <PageFrame title="Activity Logs" description="Who did what on this desk: approvals, rejections and setting changes.">
      <FilterBarForm onSubmit={() => !badRange && commit(draft)} className={FILTER_BAR}>
        <div className="flex max-lg:flex-col max-lg:gap-3 lg:flex-row lg:flex-wrap lg:items-end lg:gap-3">
          <Field label="From" className="min-w-40">
            <Input type="date" value={draft.from} onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))} />
          </Field>
          <Field label="To" className="min-w-40" error={badRange ? 'Must be on or after the start date' : undefined}>
            <Input type="date" value={draft.to} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} />
          </Field>
          <FacetFilter
            label="Action"
            value={draft.action}
            options={(options.data?.actions ?? []).map((a) => ({ value: a, label: actionLabel(a) }))}
            onChange={(action) => setDraft((d) => ({ ...d, action }))}
          />
          <Field label="Staff member" className="min-w-52" error={options.isError ? 'Could not load staff' : undefined}>
            <GlassSelect
              value={draft.actor}
              onChange={(actor) => setDraft((d) => ({ ...d, actor }))}
              options={[{ value: '', label: 'All staff' }, ...(options.data?.actors ?? [])]}
            />
          </Field>
          <FilterSearch
            label="Target ID"
            value={draft.target}
            onChange={(target) => setDraft((d) => ({ ...d, target }))}
            placeholder="First characters of the ID"
          />
          <FilterBarApply
            isDirty={!sameFilters(draft, applied) && !badRange}
            canReset={filtered || !sameFilters(draft, NO_ACTIVITY_FILTERS)}
            onReset={() => {
              setDraft(NO_ACTIVITY_FILTERS)
              commit(NO_ACTIVITY_FILTERS)
            }}
          />
        </div>
      </FilterBarForm>
      <DeskTable
        columns={activityColumns}
        rows={items}
        rowKey={(r) => String(r.id)}
        loading={q.isLoading}
        error={q.error}
        dimmed={isFilterChangeFetching(q.isFetching, q.isPlaceholderData)}
        emptyTitle={filtered ? 'No activity matches these filters' : 'No activity yet'}
        emptyDescription={filtered ? 'Widen the dates or clear a filter to see more.' : 'Staff actions from this desk will appear here.'}
        sort={sort}
        onSortChange={onSortChange}
        paging={{
          total: paging.totalItems,
          page: paging.safePage,
          pageSize,
          onPage: (p) => setParams({ page: String(p) }),
          onPageSize: (size) => setParams({ page: '1', page_size: String(size) }),
        }}
      />
    </PageFrame>
  )
}

export function EkycLogsPage() {
  const role = accessOf(useAuthStore((s) => s.user))
  const scope = canOpening(role) ? 'opening' : 'settlement'
  const canOpenReview = canOpening(role)
  const { sp, setSp, page, pageSize, setParams } = useListParams()
  const { sort, onSortChange, sortQuery } = useDeskSort(['when', 'client', 'code', 'vendor', 'status'])
  const status = facetFromSearch(sp, 'status')
  const params = { page, page_size: pageSize, include_total: true, ...sortQuery, ...facetToQuery('status', status) }
  const q = useQuery({
    queryKey: ['ekyc-logs', scope, params],
    queryFn: () => backofficeApi.ekycLogs(scope, params),
    placeholderData: keepPreviousData,
    ...visibleTabRefetch(DESK_REFETCH_MS),
  })
  const items = q.data?.items ?? []
  const paging = resolveOffsetPagination({
    page,
    pageSize,
    total: q.data?.total,
    rowCount: items.length,
    hasMore: Boolean(q.data?.has_more),
  })
  return (
    <PageFrame title="eKYC Logs" description="Identity-check attempts made by applicants, newest first.">
      <div className={FILTER_BAR}>
        <div className="max-w-xs">
      <FacetFilter
        label="Status"
        value={status}
        options={optionLabels(['PENDING', 'SKIPPED', 'PASSED'], statusLabel)}
        onChange={(next) => {
          const cur = new URLSearchParams()
          writeFacet(cur, 'status', next.values.length ? next : emptyFacet())
          cur.set('page', '1')
          cur.set('page_size', String(pageSize))
          writeSort(cur, sort)
          setSp(cur, { replace: true })
        }}
      />
        </div>
      </div>
      <DeskTable
        columns={ekycColumns(canOpenReview)}
        rows={items}
        rowKey={(r) => String(r.id)}
        loading={q.isLoading}
        error={q.error}
        dimmed={isFilterChangeFetching(q.isFetching, q.isPlaceholderData)}
        emptyTitle="No eKYC attempts"
        emptyDescription="Nothing matches this status filter."
        sort={sort}
        onSortChange={onSortChange}
        paging={{
          total: paging.totalItems,
          page: paging.safePage,
          pageSize,
          onPage: (p) => setParams({ page: String(p) }),
          onPageSize: (size) => setParams({ page: '1', page_size: String(size) }),
        }}
      />
    </PageFrame>
  )
}
